// Task #969 — Single source of truth for rendering trip pickup/dropoff
// timestamps across the renter trip page, host dashboard, and admin tools.
//
// The api-server stores renter-selected pickup/dropoff times as wall-clock
// UTC: a renter who picked "8:00 am" in any city has 'YYYY-MM-DDT08:00:00Z'
// in trips.started_at. We always format with timeZone: "UTC" so the same
// wall-clock string appears to the renter, host, and admin regardless of
// the viewer's browser timezone — and matches the time the renter actually
// picked at checkout.
//
// `timesCaptured` distinguishes a real renter-picked time from a legacy
// date-only row that just happens to read back as midnight UTC. A captured
// midnight is a real time and must render; false/undefined rows and bare
// `YYYY-MM-DD` values remain date-only.
//
// Use these helpers everywhere; do NOT call `new Date(dateStr)` directly
// against trip timestamps — it formats in the viewer's local TZ and that's
// the entire bug we're fixing.

import { isUSLocale } from "./utils";
import { isImportedTrip } from "./legacy-import";
import type { HostScheduleDisplay } from "@workspace/booking-rules";

/** Host-only response projection. Explicit null is not a fallback invitation. */
export function getHostDisplayPickupIso(trip: TripEventClockColumns & { hostSchedule?: HostScheduleDisplay }): string | null {
  return trip.hostSchedule ? trip.hostSchedule.pickup : getTripDisplayPickupIso(trip);
}
export function getHostDisplayDropoffIso(trip: { drop_off?: string | null; hostSchedule?: HostScheduleDisplay }): string | null {
  return trip.hostSchedule ? trip.hostSchedule.dropoff : trip.drop_off ?? null;
}

function pickLocale(countryCode?: string | null, override?: string): string {
  if (override) return override;
  return isUSLocale(countryCode) ? "en-US" : "en-GB";
}

// Task #2802 — Trip pickup/dropoff timestamps arrive in two shapes:
//   (a) the canonical wall-clock-UTC convention introduced in #1088:
//       `YYYY-MM-DDTHH:MM:SS.000Z`. The HH:MM is the wall-clock time
//       at the pickup location and the trailing `Z` is structural, not
//       an offset claim.
//   (b) legacy rows from before #1088, where some bookings were stored
//       as offset-bearing ISOs, e.g. `YYYY-MM-DDTHH:MM:SS+01:00`. The
//       HH:MM there is ALSO the wall-clock at pickup; the offset
//       reflects the renter's browser at booking time and is not a
//       location-of-car claim.
// In both shapes the HH:MM after the `T` is the same thing: the time
// on the car's clock at pickup. Passing an offset-bearing ISO into
// `new Date(...)` would re-interpret that HH:MM as an instant in time
// and then `toLocaleTimeString({timeZone:"UTC"})` would shift it by
// the offset — which is exactly the bug the inbox feedback flagged
// (panel showed 09:00 from a +01:00 row, modal showed 08:00 because
// it had been "fixed" with `getUTCHours`). The fix is to strip the
// offset before constructing the Date so the parser treats the
// literal HH:MM as wall-clock UTC for every downstream formatter.
function stripOffsetSuffix(ts: string): string {
  // Match `…THH:MM:SS(.fff)?` followed by either `Z` or `±HH:MM`/`±HHMM`.
  // Replace any trailing offset (including `Z`) with a canonical `Z` so
  // `new Date(...)` reads the HH:MM literally as UTC.
  return ts.replace(
    /T(\d{2}):(\d{2})(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/,
    (_m, h, m, s, ms, _off) => `T${h}:${m}${s ?? ":00"}${ms ?? ""}Z`,
  );
}

function parseTripTimestamp(ts: string): Date | null {
  // Bare 'YYYY-MM-DD' is a legacy date-only row; treat it as midnight UTC
  // so the time-portion check below cleanly falls into the date-only
  // branch regardless of the viewer's locale.
  if (/^\d{4}-\d{2}-\d{2}$/.test(ts)) {
    const d0 = new Date(`${ts}T00:00:00Z`);
    return isNaN(d0.getTime()) ? null : d0;
  }
  const d = new Date(stripOffsetSuffix(ts));
  return isNaN(d.getTime()) ? null : d;
}

function hasMeaningfulTime(ts: string, timesCaptured?: boolean | null): boolean {
  return timesCaptured === true && !/^\d{4}-\d{2}-\d{2}$/.test(ts);
}

export interface TripDateTimeOpts {
  /** When false / undefined we render date-only (no phantom midnight time). */
  timesCaptured?: boolean | null;
  /** Override the locale; defaults to en-GB (en-US for US country codes). */
  locale?: string;
  /** Country code drives the en-GB vs en-US default when locale is omitted. */
  countryCode?: string | null;
}

/**
 * "Wed, 20 May, 8:00 am" — full date + captured time, or just date when no
 * time was captured. Use on trip detail / dashboards where there's room.
 */
export function formatTripDateTime(
  ts: string | null | undefined,
  opts: TripDateTimeOpts = {},
): string {
  if (!ts) return "";
  const d = parseTripTimestamp(ts);
  if (!d) return "";
  const locale = pickLocale(opts.countryCode, opts.locale);
  const datePart = d.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  if (!hasMeaningfulTime(ts, opts.timesCaptured)) return datePart;
  const timePart = d.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
  return `${datePart}, ${timePart}`;
}

// Task #3017 — Single source of truth for "which column holds the
// pickup timestamp" on a trip row.
//
// `trips.started_at` is the canonical column. Every DB-level path
// (RPCs, create_trip_atomic, overlap math, suggestion accept) already
// writes and reads it. `trips.pick_up` is a legacy app-level mirror
// the codebase kept in sync via ad-hoc Supabase-client updates — and
// did so inconsistently, leaving stale midnight values on some rows
// that then leaked into the renter-side reservation panel as a
// missing pickup time.
//
// Reads MUST go through this helper so the convention has one home:
//   1. Prefer `started_at` (canonical).
//   2. Fall back to `pick_up` as a transitional safety net for any
//      legacy row the dev/prod backfill in Task #3017 didn't reach.
//   3. Return null when neither is set.
//
// A follow-up will drop `trips.pick_up` once the audit confirms no
// remaining reader bypasses this helper.
export interface TripPickupColumns {
  started_at?: string | null;
  pick_up?: string | null;
}

export function getTripPickupIso<T extends TripPickupColumns>(
  trip: T | null | undefined,
): string | null {
  if (!trip) return null;
  return trip.started_at ?? trip.pick_up ?? null;
}

// Task #972 — Legacy CHAUFFEURED bookings store the customer-facing EVENT
// start time in `trips.hours` (a wall-clock "HH:MM" string, e.g. "11:30"),
// while `pick_up`/`started_at` hold the HOST garage-departure time (when the
// driver leaves to collect the car) — an internal logistics detail we must
// never surface to the renter or host. These rows deliberately keep
// `times_captured = false` (migration 3125 excludes chauffeured from the flag
// flip) so no backend surface (emails/SMS/mobile) leaks the host-departure
// time. The booking-overview web surface reconstructs the event start from the
// booking date + `hours` via the display helpers below, gated strictly to
// imported/migrated chauffeured rows so native bookings are never touched.
export interface TripEventClockColumns extends TripPickupColumns {
  rental_type?: string | null;
  drop_off?: string | null;
  /** Wall-clock "HH:MM" event start for legacy chauffeured bookings. Selected
   *  from `trips.hours` under the `event_clock` alias to avoid a name clash
   *  with the numeric quoting `hours`. */
  event_clock?: string | null;
  source?: string | null;
  source_id?: number | string | null;
  platform_origin?: string | null;
}

function parseEventClock(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(raw.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  // Exactly midnight is the wall-clock "no captured time" sentinel — treat as
  // absent so we fall back to date-only rather than rendering a phantom 00:00.
  if (h === 0 && min === 0) return null;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/**
 * Task #972 — the reconstructed event-start ISO for a legacy chauffeured
 * booking, or null when the row is not an imported chauffeured booking with a
 * usable event clock (callers then fall back to getTripPickupIso). The ISO is
 * built in the same wall-clock-UTC convention as every other trip timestamp
 * (`YYYY-MM-DDTHH:MM:00.000Z`) so the existing formatters render it verbatim.
 */
export function getLegacyChauffeuredEventIso<T extends TripEventClockColumns>(
  trip: T | null | undefined,
): string | null {
  if (!trip) return null;
  if ((trip.rental_type ?? "") !== "chauffeured") return null;
  if (!isImportedTrip(trip)) return null;
  const hhmm = parseEventClock(trip.event_clock);
  if (!hhmm) return null;
  const dateSource = trip.pick_up ?? trip.started_at ?? trip.drop_off ?? null;
  if (!dateSource) return null;
  const datePart = dateSource.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
  const iso = `${datePart}T${hhmm}:00.000Z`;
  // Chronology guard: bad legacy data (event start after the booking end)
  // falls back to date-only rather than rendering start > end.
  if (trip.drop_off) {
    const end = parseTripTimestamp(trip.drop_off);
    const start = new Date(iso);
    if (end && !Number.isNaN(start.getTime()) && start.getTime() > end.getTime()) {
      return null;
    }
  }
  return iso;
}

/**
 * Display-aware pickup ISO for booking-detail surfaces:
 *   1. the legacy chauffeured EVENT start when available, so a customer never
 *      sees the host's garage-departure time;
 *   2. for a verified imported row only, its scheduled legacy `pick_up` value;
 *   3. otherwise the operational canonical reader (`started_at`, then the
 *      transitional `pick_up` fallback).
 *
 * Imported historical rows can carry a `started_at` value that was repurposed
 * during migration and no longer matches the customer-facing scheduled pickup.
 * This is deliberately display-only: availability, lifecycle, payments,
 * adjustments, and every other operational caller must continue using
 * getTripPickupIso unchanged.
 */
export function getTripDisplayPickupIso<T extends TripEventClockColumns>(
  trip: T | null | undefined,
): string | null {
  if (!trip) return null;
  const legacyEventStart = getLegacyChauffeuredEventIso(trip);
  if (legacyEventStart) return legacyEventStart;
  if (isImportedTrip(trip)) return trip.pick_up ?? trip.started_at ?? null;
  return getTripPickupIso(trip);
}

/**
 * Display-aware `times_captured`: a reconstructed legacy chauffeured event time
 * IS a genuine captured time, so the overview renders it even though the stored
 * flag stays false for these rows. Everything else returns the stored flag.
 */
export function getTripDisplayTimesCaptured<
  T extends TripEventClockColumns & { times_captured?: boolean | null },
>(trip: T | null | undefined): boolean {
  if (getLegacyChauffeuredEventIso(trip)) return true;
  return !!trip?.times_captured;
}

// Task #4884 — Some list surfaces (My Trips, host dashboard) need a date
// layout the fixed formatters above don't cover (e.g. "May 15, 2026", a
// same-month "May 15–17, 2026" range, no weekday). These two exports give
// those callers the SAME wall-clock-UTC guarantee (offset stripped, then
// formatted with timeZone:"UTC") without forcing them onto the
// weekday+short-month shape of `formatTripDate`.

/**
 * Parse a trip timestamp into a Date for UTC-based comparisons (e.g. "is the
 * pickup the same calendar day as the drop-off?"). Always compare via the
 * `getUTC*` accessors on the returned Date — never the local `get*` ones —
 * so the day boundary isn't shifted by the viewer's timezone. Returns null
 * when the input is missing / unparseable.
 */
export function parseTripDate(ts: string | null | undefined): Date | null {
  if (!ts) return null;
  return parseTripTimestamp(ts);
}

/**
 * Format a trip timestamp's DATE with caller-supplied Intl options, always
 * pinned to `timeZone: "UTC"` (offset stripped first). Use when a surface
 * needs a bespoke date layout; for the time portion pair this with
 * `formatTripTime` (which already honours `timesCaptured`).
 */
export function formatTripDateCustom(
  ts: string | null | undefined,
  locale: string,
  intlOpts: Intl.DateTimeFormatOptions,
): string {
  if (!ts) return "";
  const d = parseTripTimestamp(ts);
  if (!d) return "";
  return d.toLocaleDateString(locale, { ...intlOpts, timeZone: "UTC" });
}

/** "Wed, 20 May" — short date, no year, no time. */
export function formatTripDate(
  ts: string | null | undefined,
  opts: TripDateTimeOpts = {},
): string {
  if (!ts) return "";
  const d = parseTripTimestamp(ts);
  if (!d) return "";
  const locale = pickLocale(opts.countryCode, opts.locale);
  return d.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** "Wed, 20 May 2025" — short date WITH year, no time. */
export function formatTripDateWithYear(
  ts: string | null | undefined,
  opts: TripDateTimeOpts = {},
): string {
  if (!ts) return "";
  const d = parseTripTimestamp(ts);
  if (!d) return "";
  const locale = pickLocale(opts.countryCode, opts.locale);
  return d.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * "8:00 am" — JUST the time, or empty string when no time was captured.
 * Callers that want to show a placeholder when the time isn't captured
 * should fall back themselves; returning "" lets the existing dashboard
 * layouts stay tidy.
 */
export function formatTripTime(
  ts: string | null | undefined,
  opts: TripDateTimeOpts = {},
): string {
  if (!ts) return "";
  const d = parseTripTimestamp(ts);
  if (!d) return "";
  if (!hasMeaningfulTime(ts, opts.timesCaptured)) return "";
  const locale = pickLocale(opts.countryCode, opts.locale);
  return d.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
}

/** "MAY 20" — uppercase month + day, no year, no time. Used by step rails. */
export function formatTripMonthDay(
  ts: string | null | undefined,
  opts: TripDateTimeOpts = {},
): string {
  if (!ts) return "";
  const d = parseTripTimestamp(ts);
  if (!d) return "";
  const locale = pickLocale(opts.countryCode, opts.locale);
  return d.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).toUpperCase();
}

/**
 * "Wed, 20 May → Fri, 22 May" or "Wed, 20 May, 8:00 am → Fri, 22 May, 11:00 am"
 * depending on whether times were captured. Used in trip lists / admin
 * tables / emails to give the host a one-line "what was booked".
 */
export function formatTripDateRange(
  startTs: string | null | undefined,
  endTs: string | null | undefined,
  opts: TripDateTimeOpts = {},
): string {
  const left = formatTripDateTime(startTs, opts);
  const right = formatTripDateTime(endTs, opts);
  if (!left && !right) return "";
  if (!left) return right;
  if (!right) return left;
  return `${left} → ${right}`;
}

// Task #1063 / #1088: the "Suggest a change" modal must round-trip times in
// the same wall-clock-UTC convention the rest of the system uses. The two
// helpers below are colocated with the formatters so the convention has a
// single home — and so we can defend it with unit tests independent of the
// massive TripDetailPage component.

/**
 * Build a wall-clock-UTC ISO from a `YYYY-MM-DD` date and `HH:MM` time the
 * renter typed into the modal. Returns null when either piece is missing so
 * the caller keeps "no time change proposed" semantics.
 *
 * Constructed by string interpolation — NOT through `new Date(...)` — so the
 * runner / browser timezone never shifts the hour. The api-server reads the
 * proposed time back via `.slice(11, 16)` and stores it in `started_at` /
 * `drop_off`, which the rest of the app then renders with `timeZone: "UTC"`.
 */
export function composeAdjIso(date: string, time: string): string | null {
  if (!date || !time) return null;
  const [hh, mm] = time.split(":").map((s) => Number(s));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  const padded = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  return `${date}T${padded}:00.000Z`;
}

/**
 * Pre-fill the modal's `HH:MM` input from a stored trip timestamp.
 *
 * Returns `""` when no time was ever captured (legacy date-only bookings)
 * so the field stays blank instead of surfacing a phantom "00:00" that the
 * renter never picked.
 *
 * Task #2802 — Read HH:MM by literal substring match on the ISO instead of
 * via a Date object. This makes the read symmetric with the rest of the
 * trip-datetime helpers (which strip the offset before parsing) and with
 * `composeAdjIso`'s string-interpolation write. Crucially it handles BOTH
 * shapes the DB stores:
 *   - `2026-05-19T05:00:00.000Z`   (wall-clock-UTC convention, #1088)   → 05:00
 *   - `2026-05-19T05:00:00+01:00`  (legacy offset-bearing rows)         → 05:00
 * In both cases the HH:MM after `T` is the time the renter picked at
 * checkout — the literal wall clock at the pickup location. The previous
 * `getUTCHours` implementation would re-interpret offset-bearing rows as
 * an instant and shift the displayed hour, which is the exact bug filed.
 */
export function prefillAdjTime(
  ts: string | null | undefined,
  timesCaptured: boolean | null | undefined,
): string {
  if (!timesCaptured || !ts) return "";
  const m = ts.match(/T(\d{2}):(\d{2})/);
  if (!m) return "";
  // Defensive: if the Date constructor would have rejected the ISO
  // outright, treat the input as unparseable and surface "" so the
  // modal degrades to a blank field rather than seeding from garbage.
  if (isNaN(new Date(`2000-01-01T${m[1]}:${m[2]}:00Z`).getTime())) return "";
  return `${m[1]}:${m[2]}`;
}

/**
 * Convert "HH:MM" (24h) to "h:MM am/pm" so the modal's stored 24h string
 * can be handed to the checkout TimePicker which speaks 12h. Returns the
 * supplied fallback when the input is empty/malformed so callers can show
 * a sensible default in the trigger label.
 */
export function hhmmTo12h(hhmm: string, fallback: string): string {
  if (!hhmm) return fallback;
  const [h, m] = hhmm.split(":").map((s) => Number(s));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return fallback;
  const period = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

/**
 * Task #3392 — Render a host-acceptance offer deadline as a short,
 * timezone-proof relative countdown ("Expires in 7 hours", "Expires in
 * 2 days", "Expires soon", "Expired") for the Booking Management list.
 *
 * Unlike the wall-clock-UTC trip timestamps above, `offer_expiration_date`
 * is a genuine `timestamptz` instant (see api-server `isOfferExpired`), so
 * here we DO compare instants directly: the difference between the deadline
 * and `now` is the same number of milliseconds in every timezone, which is
 * exactly what makes the primary label TZ-independent. The viewer's locale
 * only affects the absolute timestamp we surface separately on hover.
 *
 * Edge handling:
 *   - A deadline at or in the past reads "Expired" (never a negative or
 *     "in -3 hours" string).
 *   - Under ~2 minutes out reads "Expires soon" rather than "in 1 minute".
 *   - null / unparseable input returns "" so the caller's render guard
 *     (status === "pending" && offer_expiration_date) stays in control.
 */
export function formatOfferExpiryRelative(
  expirationDate: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!expirationDate) return "";
  const expMs = new Date(expirationDate).getTime();
  if (!Number.isFinite(expMs)) return "";
  const diffMs = expMs - now;
  if (diffMs <= 0) return "Expired";

  // Floor (not round) each bucket so we never overstate the time left —
  // "Expires in 1 day" when only 23h31m remain would lull a host into
  // missing the deadline. We always round the urgency DOWN.
  if (diffMs < 3_600_000) {
    const minutes = Math.floor(diffMs / 60_000);
    return minutes < 2 ? "Expires soon" : `Expires in ${minutes} minutes`;
  }
  if (diffMs < 86_400_000) {
    const hours = Math.floor(diffMs / 3_600_000);
    return `Expires in ${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const days = Math.floor(diffMs / 86_400_000);
  return `Expires in ${days} day${days === 1 ? "" : "s"}`;
}

/**
 * Task #4618 — Render a custom-quote expiry deadline as an absolute
 * date + time ("Jun 26, 8:14 AM") in the viewer's locale and timezone.
 *
 * The quote `expires_at` is a genuine `timestamptz` instant (the server
 * stores `min(sent + 48h, pickup − 1h)`), so we format the instant directly
 * in the viewer's zone — the same way the conversation renders message
 * timestamps. The card previously formatted this with a time-only helper,
 * which dropped the date and made a quote with ~3 days left look like it
 * expired in minutes ("Expires 08:14 AM"). Including the date makes the
 * real deadline unambiguous, and a same-day (pickup-clamped) expiry still
 * reads sensibly because the date is always present.
 *
 * Returns "" for missing / unparseable input so the caller's render guard
 * (effectiveStatus === "sent" && expiresAt) stays in control.
 */
export function formatQuoteExpiry(
  expiresAt: string | null | undefined,
  locale: string,
): string {
  if (!expiresAt) return "";
  const d = new Date(expiresAt);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Task #4621 — Structured relative countdown for a custom-quote deadline.
 *
 * Returns the urgency "bucket" (and, where relevant, the integer count) for
 * `expires_at` so the caller can render a localized, live-updating countdown
 * ("Expires in 3 days" → "Expires in 2 hours" → "Expires soon") with the
 * number animated by the odometer-style `RollingNumber`. This is the
 * i18n-friendly, animation-friendly sibling of `formatOfferExpiryRelative`
 * (which bakes an English string): the bucketing rules are identical so both
 * surfaces feel the same, but here we hand back data instead of prose so the
 * component owns the translation and the digit reel.
 *
 * Like `formatOfferExpiryRelative`, the quote `expires_at` is a genuine
 * `timestamptz` instant, so comparing it against `now` is timezone-proof: the
 * remaining milliseconds are identical in every viewer's zone.
 *
 * Buckets (each floored DOWN so we never overstate the time left):
 *   - at/after the deadline  → { kind: "expired" }
 *   - under ~2 minutes out   → { kind: "soon" }
 *   - 2–59 minutes out       → { kind: "minutes", count }
 *   - 1–23 hours out         → { kind: "hours", count }
 *   - 1+ days out            → { kind: "days", count }
 *
 * Returns null for missing / unparseable input so the caller's render guard
 * (effectiveStatus === "sent" && expiresAt) stays in control.
 */
export type QuoteCountdown =
  | { kind: "expired" }
  | { kind: "soon" }
  | { kind: "minutes"; count: number }
  | { kind: "hours"; count: number }
  | { kind: "days"; count: number };

export function getQuoteCountdown(
  expiresAt: string | null | undefined,
  now: number = Date.now(),
): QuoteCountdown | null {
  if (!expiresAt) return null;
  const expMs = new Date(expiresAt).getTime();
  if (!Number.isFinite(expMs)) return null;
  const diffMs = expMs - now;
  if (diffMs <= 0) return { kind: "expired" };

  // Floor (not round) each bucket so we never overstate the time left — the
  // same discipline as formatOfferExpiryRelative.
  if (diffMs < 3_600_000) {
    const minutes = Math.floor(diffMs / 60_000);
    return minutes < 2 ? { kind: "soon" } : { kind: "minutes", count: minutes };
  }
  if (diffMs < 86_400_000) {
    return { kind: "hours", count: Math.floor(diffMs / 3_600_000) };
  }
  return { kind: "days", count: Math.floor(diffMs / 86_400_000) };
}

/**
 * Exact, second-resolution breakdown of the time left until an offer/quote
 * deadline — the data behind a LIVE odometer countdown clock (`Dd HH:MM:SS`).
 *
 * `getQuoteCountdown` buckets the remaining time into days/hours/minutes, which
 * is why a deadline still days away looks frozen (the figure only changes once
 * a day). This returns every segment instead, so the caller can render a clock
 * that visibly ticks every second.
 *
 * Like the bucketed helpers, the deadline (`offer_expiration_date` /
 * `expires_at`) is a genuine `timestamptz` instant, so the millisecond diff is
 * identical in every viewer's timezone.
 *
 * Returns null for missing / unparseable / already-expired input so the
 * caller's render guard (and the parent's terminal-status logic) stays in
 * control — a lapsed deadline never renders a zero or negative clock.
 */
export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Whole milliseconds remaining (always > 0 when non-null). */
  totalMs: number;
}

export function getCountdownParts(
  expiresAt: string | null | undefined,
  now: number = Date.now(),
): CountdownParts | null {
  if (!expiresAt) return null;
  const expMs = new Date(expiresAt).getTime();
  if (!Number.isFinite(expMs)) return null;
  const diffMs = expMs - now;
  if (diffMs <= 0) return null;

  const totalSec = Math.floor(diffMs / 1000);
  return {
    days: Math.floor(totalSec / 86_400),
    hours: Math.floor((totalSec % 86_400) / 3_600),
    minutes: Math.floor((totalSec % 3_600) / 60),
    seconds: totalSec % 60,
    totalMs: diffMs,
  };
}

/**
 * Default pick-up / drop-off times seeded by rental type, in "h:MM am/pm"
 * form. Single source of truth shared by the renter BookingWidget and the
 * host "Build a trip" modal so the two surfaces seed identical defaults:
 *   - self-drive ("driving"): a full day, 8:00 am → 10:00 pm
 *   - chauffeured / event:    a tight morning window, 8:00 am → 10:00 am
 *     (still a valid span so the derived hours stay positive)
 */
export function getDefaultTimes(
  rentalType: "driving" | "chauffeured" | "event",
): { pickup: string; dropoff: string } {
  if (rentalType === "driving") return { pickup: "8:00 am", dropoff: "10:00 pm" };
  return { pickup: "8:00 am", dropoff: "10:00 am" };
}

/**
 * Convert "h:MM am/pm" back to "HH:MM" (24h). Returns "" when the input
 * isn't in the expected shape so the caller treats it as "no time picked".
 */
export function time12ToHhmm(s: string): string {
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (!m) return "";
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const p = m[3].toLowerCase();
  if (p === "pm" && h !== 12) h += 12;
  if (p === "am" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}
